import { type FC, useRef } from 'react';
import cn from 'classnames';
import { type Swiper as SwiperInstance } from 'swiper';

import { useNextSpecContractTime } from '@/common/hooks/next-spec-contract-time';
import { type SpecContractStatusType } from '@/common/types/contract';

import { AdditionalTimeInfo } from '../../AdditionalTimeInfo/AdditionalTimeInfo';
import { ContractButtons } from '../../ContractButtons/ContractButtons';
import { ContractDescription } from '../../ContractDescription/ContractDescription';
import { ContractExpiredText } from '../../ContractExpiredText/ContractExpiredText';
import { ContractInfo } from '../../ContractInfo/ContractInfo';
import { ContractRewardsList } from '../../ContractRewardsList/ContractRewardsList';
import { ContractSubtitle } from '../../ContractSubtitle/ContractSubtitle';
import { OpenContractTimer } from '../../ContractTimer/OpenContractTimer';
import WarningTimer from '../../WarningTimer/WarningTimer';
import { BlitzContractTerms } from '../BlitzContractTerms/BlitzContractTerms';
import { StepsContext } from '../context';

interface BlitzContractDetailProps {
  kind: 'Blitz' | 'BlitzPlus';
  status: SpecContractStatusType;
}

const BlitzContractDetail: FC<BlitzContractDetailProps> = ({
  kind,
  status,
}) => {
  const swiper = useRef<SwiperInstance | null>(null);
  const timeToNextContract = useNextSpecContractTime(kind);
  const hours = Math.floor((timeToNextContract || 0) / 3600);
  const isContractExpired = status === 'expired';

  return (
    <StepsContext.Provider value={{ swiper }}>
      <div className="w-full flex flex-col items-center relative z-[2]">
        <OpenContractTimer isExtendsTimer={isContractExpired} kind={kind} />
        <BlitzContractTerms
          kind={kind}
          swiperRef={swiper}
          isContractExpired={isContractExpired}
        />
        {/* <GrandContractTerms /> */}
        {isContractExpired ? (
          <div className="flex flex-col items-center mt-6 mb-8">
            <ContractExpiredText />
            <AdditionalTimeInfo kind={kind} className="mt-5" />
          </div>
        ) : (
          <>
            {hours > 3 ? (
              <>
                <ContractSubtitle kind={kind} />
                <ContractDescription kind={kind} />
              </>
            ) : (
              <WarningTimer
                timer={timeToNextContract || 0}
                classNames="mt-[40px]"
              />
            )}
          </>
        )}
        {!isContractExpired && (
          <>
            <ContractRewardsList
              kind={kind}
              className={cn(status === 'inProgress' ? 'mb-8' : 'mb-3', 'mt-5')}
            />
            <ContractInfo kind={kind} />
          </>
        )}
        <ContractButtons kind={kind} isContractExpired={isContractExpired} />
      </div>
    </StepsContext.Provider>
  );
};

export { BlitzContractDetail };
